import { Combobox } from "design-system/organisms/Combobox"
import { cn } from "design-system/utils"
import { Plus } from "lucide-react"
import { useQRsQuery } from "modules/campaigns/hooks/sections/qr/useQRsQuery"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { QRsResponseDataSchema } from "schema"
import { z } from "zod"

type Option =
  | (React.ComponentProps<typeof Combobox>["options"][number] & {
      data?: z.infer<typeof QRsResponseDataSchema>[number]
    })
  | undefined

type QRFormSelectorProps = {
  onChange: (value: Option) => void
  placeholder: string
  emptyPlaceholder: string
  searchPlaceholder: string
  className?: string
  avoidDisplaySelected?: boolean
  hasCreateNew?: boolean
  value?: string | null
  isDisabled?: boolean
}

const QRFormSelector = ({
  onChange,
  placeholder,
  emptyPlaceholder,
  searchPlaceholder,
  className,
  avoidDisplaySelected,
  hasCreateNew,
  value,
  isDisabled,
}: QRFormSelectorProps) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue({
    value: searchQuery,
  })
  const [selectedValue, setSelectedValue] = useState<Option>(undefined)

  const { data: qrs, isPending } = useQRsQuery({
    query: {
      query: debouncedSearchQuery,
      page: 1,
      total: 20,
      sortBy: { field: "name", order: "asc" },
      includeIds: value ? [value] : undefined,
    },
  })

  const options = useMemo(() => {
    if (!qrs) return []

    return qrs.map((qr) => {
      const qrName = qr.name ? qr.name : t("campaigns.templates.qr.untitled")
      return {
        label: qrName,
        value: qr.id,
        data: qr,
      }
    })
  }, [qrs, t])

  function onComboboxChange(option: Option) {
    if (!avoidDisplaySelected) {
      setSelectedValue(option)
    }
    onChange(option)
  }

  useEffect(() => {
    if (value && qrs) {
      const result = options.find((e) => e.value === value)

      if (result) {
        setSelectedValue(result)
      }
    }
  }, [value, qrs, options])

  return (
    <div className={cn("flex items-center space-x-4", className)}>
      <Combobox
        options={options}
        value={selectedValue}
        onChange={onComboboxChange}
        placeholder={placeholder}
        emptyPlaceholder={emptyPlaceholder}
        searchPlaceholder={searchPlaceholder}
        disabled={isPending || isDisabled}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        className="w-full"
        actions={
          hasCreateNew
            ? [
                {
                  icon: Plus,
                  text: t("campaigns.trigger.metadata.qr.createNewQR"),
                  to: "/settings/channels/qr/create",
                  target: "_blank",
                },
              ]
            : undefined
        }
      />
    </div>
  )
}

export default QRFormSelector
